"use client"

import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// ---------- Sides ----------
// Sources:
// - ANSI/BHMA A156.5 — Cylindrical and Tubular Locks performance grading
//   (Grade 1 = commercial heavy-duty, Grade 2 = light commercial,
//   Grade 3 = residential).
// - NSF/CDC food-safety guidance for medical/pharma cold-storage access
//   logging (Vaccine Storage and Handling Toolkit).
// - Manufacturer-published install requirements for adhesive,
//   keyed-cam, and electronic refrigerator locks.

interface Side {
  key: "physical" | "keypad" | "smart"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_PHYSICAL: Side = {
  key: "physical",
  name: "Physical key (cam lock)",
  tag: "The simplest serious lock",
  icon: "🔑",
  color: "#0469f0",
  pros: [
    "No batteries — works during power outages",
    "Cam locks rated to ANSI/BHMA Grade 2 are field-tested for light commercial use",
    "Hardest to defeat without obvious damage",
    "Lowest cost for true security ($30–80)",
  ],
  cons: [
    "Requires drilling — typically voids appliance warranty",
    "Lose the key, lose access (or pay a locksmith)",
    "No audit trail of who opened it",
    "Single shared key — no per-user revocation",
  ],
}

const SIDE_KEYPAD: Side = {
  key: "keypad",
  name: "Digital keypad / combination",
  tag: "The flexible middle ground",
  icon: "⌨",
  color: "#ff9951",
  pros: [
    "No keys to lose or duplicate",
    "Combination is changeable when staff turns over",
    "Some models log access locally",
    "Mid-range price ($25–80)",
  ],
  cons: [
    "Battery-dependent on electronic models",
    "Combo can be observed and shared",
    "Not rated for medical/pharmacy compliance use",
    "Some adhesive-mounted units fail under daily use",
  ],
}

const SIDE_SMART: Side = {
  key: "smart",
  name: "Smart / app-controlled",
  tag: "The audit-trail pick",
  icon: "📱",
  color: "#7c3aed",
  pros: [
    "Per-user codes — revoke individuals, not whole household",
    "App-side access log timestamps every open",
    "Remote unlock for delivery / family",
    "Required-style fit for vaccine and pharmacy storage logging (NSF / CDC guidance)",
  ],
  cons: [
    "Most expensive ($80–200+)",
    "Battery-dependent — typically 6–12 months on alkaline",
    "Requires WiFi or Bluetooth for full functionality",
    "Firmware updates can break older models",
  ],
}

interface Scenario {
  who: string
  winner: "physical" | "keypad" | "smart"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "Toddler-proofing a kitchen fridge",
    winner: "keypad",
    reason: "Adhesive strap or magnetic combo locks are designed exactly for this — no drilling, no batteries, no warranty risk.",
  },
  {
    who: "Garage beverage fridge in a household with teens",
    winner: "physical",
    reason: "A cam lock that requires a key beats a 4-digit combo a teenager will guess in two tries.",
  },
  {
    who: "Office break-room shared fridge",
    winner: "keypad",
    reason: "Rotate the combo when staff changes; no key tracking, no per-person hardware.",
  },
  {
    who: "Vaccine / pharmacy / lab fridge",
    winner: "smart",
    reason: "CDC Vaccine Storage and Handling Toolkit requires an access record for cold-chain compliance. App-logged smart locks are the only category that delivers this out of the box.",
  },
  {
    who: "Commercial restaurant walk-in",
    winner: "physical",
    reason: "ANSI/BHMA Grade 1 cylinder cam locks are the long-standing commercial standard — durable, repairable, no battery dependency.",
  },
  {
    who: "Vacation rental that swaps occupants weekly",
    winner: "smart",
    reason: "Per-stay codes that auto-expire are the only category that scales with high turnover.",
  },
]

// ---------- Page ----------

export function RefrigeratorLockComparisonPageClient() {
  return (
    <ToolPageShell
      toolName="Refrigerator Lock Comparison"
      heroEyebrow="Specialty"
      heroTitlePre="Physical key,"
      heroTitleItalic="keypad,"
      heroTitlePost="or smart lock?"
      heroSubtitle="Three lock categories, three different security and convenience tradeoffs. Here's the honest breakdown — graded against ANSI/BHMA standards and CDC compliance needs."
      heroMeta={["⏱ ~3 min read", "ANSI/BHMA grading + NSF/CDC guidance"]}
      heroShare={<ToolShareBar title="Refrigerator Lock Comparison" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Pick a <em className="italic text-[#ff9951] font-bold">physical cam lock</em> for
                serious security or commercial use. Pick{" "}
                <em className="italic text-[#ff9951] font-bold">keypad</em> for shared spaces and
                child-proofing. Pick <em className="italic text-[#ff9951] font-bold">smart</em>{" "}
                whenever you need an access log.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "What does ANSI/BHMA Grade mean for a fridge lock?",
          answer:
            "ANSI/BHMA A156.5 grades cylindrical and cam locks by cycle life and force resistance. Grade 1 = commercial heavy-duty (800,000+ cycles). Grade 2 = light commercial. Grade 3 = residential. For a household kitchen fridge, Grade 3 is plenty; for a restaurant walk-in or commercial unit, look for Grade 1.",
        },
        {
          question: "Will adding a lock void my fridge's warranty?",
          answer:
            "Drilling for a cam lock or screwed-in latch typically voids the manufacturer warranty on most major brands. Adhesive-mount locks (straps, magnetic, electronic with adhesive base) generally don't, but read your specific warranty terms.",
        },
        {
          question: "Does a vaccine or pharmacy fridge need a smart lock?",
          answer:
            "The CDC Vaccine Storage and Handling Toolkit requires that access to vaccine storage be controlled and logged. Manual paper logs are still acceptable, but app-logging smart locks are the lowest-overhead way to satisfy the requirement at scale.",
        },
        {
          question: "How long do batteries last on a smart fridge lock?",
          answer:
            "Manufacturer-published battery life on alkaline cells typically runs 6–12 months under daily use. Cold environments (garage, walk-in) shorten battery life — keep spares on hand and change before low-battery alerts trigger lockout.",
        },
        {
          question: "Can a determined adult defeat any of these?",
          answer:
            "Yes — none of these are anti-burglar locks. They're access-control devices designed to stop kids, casual snooping, accidental opens during transit, and to satisfy compliance logging. For true theft prevention, the appliance needs to be in a locked room.",
        },
      ]}
      relatedTools={[
        { title: "Refrigerator Repair Cost Estimator", category: "Specialty", description: "Repair vs replace math.", href: "/tools/refrigerator-repair-cost-estimator", time: "~1 min" },
        { title: "Refrigerator Lifespan Calculator", category: "Specialty", description: "Years of life remaining.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Find Your Fridge", category: "Quiz", description: "Five questions to a starting product group.", href: "/tools/find-your-fridge", time: "~90 sec" },
        { title: "Refrigerator Size Calculator", category: "Size & Capacity", description: "Right cu ft for your household.", href: "/tools/refrigerator-size-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Ready to shop a fridge that fits the lock?",
        body: "Browse counter-depth refrigerators — the flat-front profile is the easiest to retrofit with any lock category.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      {/* ============ SPEC FIGHT (3-way) ============ */}
      <section className="bg-white pt-8 md:pt-12">
        <div className="max-w-[880px] mx-auto px-6 md:px-8">
          <GuideTool
            eyebrow="Interactive selector"
            title="Which lock fits your situation?"
            intro="Who you're keeping out and what you'll drill decide it. Three questions."
            questions={[
                          {
                            id: "who",
                            label: "Who are you locking out?",
                            options: [
                              { value: "kids", label: "Young kids" },
                              { value: "shared", label: "Office / roommates" },
                              { value: "outdoor", label: "Outdoor or public space" }
                            ],
                          },
                          {
                            id: "door",
                            label: "What door style?",
                            options: [
                              { value: "single", label: "Single door" },
                              { value: "french", label: "French / double doors" },
                              { value: "glass", label: "Sliding glass / display" }
                            ],
                          },
                          {
                            id: "install",
                            label: "Install preference?",
                            options: [
                              { value: "adhesive", label: "No tools \u2014 adhesive" },
                              { value: "drilled", label: "Drilled, permanent" }
                            ],
                          }
            ]}
            compute={(a) => {
                          if (a.who === "kids") {
                            return { tone: "good", verdict: "Adhesive strap lock", detail: "For childproofing, an adhesive strap with an adult-release button is the fit \u2014 tool-free, damage-free, and " + (a.door === "french" ? "one strap spans both French doors at the seam." : "strong enough that toddlers can't defeat it while adults open one-handed.") }
                          }
                          if (a.who === "outdoor") {
                            return { tone: "warn", verdict: "Drilled hasp + weatherproof padlock", detail: "Outdoors, adhesives fail with heat and moisture \u2014 a through-bolted stainless hasp with a weather-rated padlock is the only version that still deters a determined adult after a summer. " + (a.door === "glass" ? "For sliding glass display doors, use a ratcheting display-case lock on the track instead." : "") }
                          }
                          const cable = a.door === "french" ? "a cable lock threaded through both handles" : a.install === "drilled" ? "a drilled hasp with a combination padlock" : "an adhesive hasp with a combination padlock"
                          return { tone: "good", verdict: "Combination lock over keyed", detail: "For shared spaces, go combination \u2014 no keys to copy or lose \u2014 via " + cable + ". Change the code when housemates change; that is the real security event in an office kitchen." }
                        }}
            ctaLabel="Pick my lock"
            footnote="Security and install trade-offs are compared in full below."
          />
        </div>
      </section>

      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1180px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Three-way comparison" title="The spec fight" right="Security, install, audit log" />

          <div className="grid md:grid-cols-3 gap-5">
            {[SIDE_PHYSICAL, SIDE_KEYPAD, SIDE_SMART].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6"
                style={{
                  border: `2px solid ${s.color}`,
                  background: `${s.color}08`,
                }}
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: `${s.color}30` }}>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: s.color }}>
                      Side {String.fromCharCode(65 + i)}
                    </div>
                    <div className="text-[20px] font-black tracking-tight text-gray-900 leading-tight">{s.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                  <div className="text-3xl" style={{ color: s.color }}>{s.icon}</div>
                </div>

                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">✓ Pros</div>
                  <ul className="space-y-1.5 text-[13px] text-gray-700 mb-4">
                    {s.pros.map((p) => <li key={p} className="leading-[1.4]">{p}</li>)}
                  </ul>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">✕ Cons</div>
                  <ul className="space-y-1.5 text-[13px] text-gray-500">
                    {s.cons.map((c) => <li key={c} className="leading-[1.4]">{c}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => {
              const color = s.winner === "physical" ? "#0469f0" : s.winner === "keypad" ? "#ff9951" : "#7c3aed"
              const label = s.winner === "physical" ? "Physical key" : s.winner === "keypad" ? "Keypad" : "Smart lock"
              return (
                <div
                  key={s.who}
                  className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
                >
                  <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">{s.who}</h3>
                    <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                  </div>
                  <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                      style={{ background: color }}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Shop the right base unit"
          title="Best counter-depth refrigerators"
          description="Counter-depth designs with flat doors are the easiest to retrofit with any lock category."
          collectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we graded" title="Methodology" />
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              <strong>Lock grading.</strong> Cycle-life and force-resistance grades are drawn from{" "}
              <strong>ANSI/BHMA A156.5</strong>, the cylindrical and tubular lock performance
              standard. Most refrigerator-specific cam locks sold to households fall in
              residential Grade 3; light-commercial cam locks are Grade 2; full commercial walk-in
              locks are Grade 1.
            </p>
            <p>
              <strong>Compliance use.</strong> For vaccine, pharmacy, lab, and medical
              cold-storage applications, access logging is required by the{" "}
              <strong>CDC Vaccine Storage and Handling Toolkit</strong> and analogous{" "}
              <strong>NSF</strong> food-handling guidance. Smart locks with timestamped access
              logs are the lowest-overhead way to satisfy this; paper logs are still acceptable
              but operationally heavier.
            </p>
            <p>
              <strong>Install impact on warranty.</strong> Drill-mounted cam locks typically void
              the manufacturer&apos;s appliance warranty. Adhesive-mount keypads, straps, and electronic
              locks generally do not — but read your specific warranty terms.
            </p>
            <p>
              <strong>What&apos;s not modeled.</strong> Theft-grade attacks (pry bars, drills,
              destructive entry) are out of scope for any consumer-grade refrigerator lock. For
              theft prevention, the appliance must be in a locked room.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges where access control matters most"
          description="Garage, dorm, and beverage units — the form factors where a lock is most often added after the fact."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
